import { Pencil, ShieldAlert, ShieldCheck, Trash2, TriangleAlert } from 'lucide-react';
import React from 'react';
// Utiliser le type API BlockerRead
import { BlockerRead } from '../../types';

import MarkdownDisplay from '../Common/MarkdownDisplay';
import { useTranslation } from 'react-i18next';

interface BlockerCardProps {
  blocker: BlockerRead; // Utiliser BlockerRead
  onEdit: (blocker: BlockerRead) => void; // Attendre BlockerRead
  onDelete: (blockerId: string) => void;
  isReadOnly?: boolean; // Mode lecture seule
}

// Helper pour obtenir la couleur et l'icône de la criticité
// Adapter le paramètre et les cas pour correspondre à BlockerRead.criticality (string | null)
const getCriticiteStyle = (criticite: string | null | undefined, t: (key: string) => string) => {
  switch (criticite?.toLowerCase()) { // Utiliser toLowerCase pour insensibilité à la casse
    case 'high': // Adapter les valeurs attendues
    case 'haute': // Garder 'haute' pour compatibilité si nécessaire
      return {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        icon: <TriangleAlert className="h-4 w-4 mr-1" />,
        text: t('blocker.criticality.high'),
      };
    case 'medium': // Adapter les valeurs attendues
    case 'moyenne': // Garder 'moyenne'
      return {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        icon: <ShieldAlert className="h-4 w-4 mr-1" />,
        text: t('blocker.criticality.medium'),
      };
    case 'low': // Adapter les valeurs attendues
    case 'faible': // Garder 'faible'
      return {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        icon: <ShieldCheck className="h-4 w-4 mr-1" />,
        text: t('blocker.criticality.low'),
      };
    default:
      return {
        bgColor: 'bg-muted',
        textColor: 'text-muted-foreground',
        icon: null,
        text: t('global.notDefined'),
      };
  }
};

const BlockerCard: React.FC<BlockerCardProps> = ({ blocker, onEdit, onDelete, isReadOnly = false }) => {
  const { t } = useTranslation();
  // Utiliser blocker.criticite
  const criticiteStyle = getCriticiteStyle(blocker.criticite, t);

  return (
    <div className="bg-card shadow overflow-hidden sm:rounded-lg border border-border mb-4">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex items-start justify-between">
          {/* Afficher blocker.description comme titre */}
          <h3 className="text-base font-semibold leading-6 text-foreground flex-1 mr-4">
            {blocker.description}
          </h3>
          {!isReadOnly && (
            <div className="ml-auto flex-shrink-0 flex items-center space-x-2">
              <button
                onClick={() => onEdit(blocker)}
                className="text-primary hover:text-primary/80 p-1 rounded hover:bg-accent"
                aria-label={t('blocker.actions.edit')}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={() => onDelete(blocker.id)}
                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-accent"
                aria-label={t('global.delete')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        {/* Affichage Criticité */}
        <div className="mt-2">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${criticiteStyle.bgColor} ${criticiteStyle.textColor}`}
          >
            {criticiteStyle.icon}
            {t('blocker.criticality.label')}: {criticiteStyle.text}
          </span>
        </div>
        {/* Le deuxième affichage de la criticité est supprimé */}
      </div>
      {/* Affichage Stratégie de Mitigation (utiliser strategie_mitigation) */}
      {(blocker.strategie_mitigation) && (
        <div className="border-t border-border px-4 py-4 sm:px-6">
          <h4 className="text-sm font-medium text-muted-foreground mb-1">{t('blocker.mitigationStrategy.title')}</h4>
          <MarkdownDisplay 
            content={blocker.strategie_mitigation} 
            emptyMessage={t('global.notDefined')} 
          />
        </div>
      )}
    </div>
  );
};

export default BlockerCard;
